/**
 * The account links in every page's header.
 *
 * One module rather than three copies, because the three surfaces already
 * disagreed about their headers once and a nav that says "sign in" on one page
 * and nothing on another is how somebody concludes accounts are broken.
 *
 * **It renders signed-out first and corrects itself.** `account()` is a network
 * call, and a header that waits for it flashes empty on every page load. So the
 * cheap local answer paints immediately and the server's answer replaces it,
 * which is the same shape as the auto-direct box: local for speed, server for
 * truth.
 */
import { account, looksSignedIn, signOut } from './session.js';
import { navLinks, type NavState } from './view.js';

function render(state: NavState): void {
  const host = document.getElementById('nav');
  if (!host) return;
  host.replaceChildren();
  for (const link of navLinks(state)) {
    if (link.kind === 'text') {
      const span = document.createElement('span');
      span.className = `nav-you ${link.className ?? ''}`.trim();
      span.textContent = link.label;
      host.appendChild(span);
      continue;
    }
    const a = document.createElement('a');
    a.textContent = link.label;
    a.href = link.href;
    if (link.className) a.className = link.className;
    if (link.kind === 'signout') {
      a.addEventListener('click', (ev) => {
        ev.preventDefault();
        signOut();
        // Reload rather than repaint: a publish page holding a token in flight
        // should not keep acting as an account the person just left.
        location.reload();
      });
    }
    host.appendChild(a);
  }
}

/** Paint the nav, then correct it from the server. Safe to call on any page. */
export function mountNav(): void {
  render({ signedIn: looksSignedIn(), handle: null });
  void account().then((acct) => {
    render({ signedIn: acct !== null, handle: acct?.handle ?? null });
  });
}
